import { z } from "zod";
import { JiraDraft, JiraIssue, JiraIssueCreate } from "../schemas/jira";
import { request, validateInput } from "../request";
import type { ActionReturnType } from "../types";

export class JiraIssuesEndpoint {
  public static readonly basePath = "jira-issues";
  public static readonly schemas = {
    draft: {
      query: z.object({
        cycle_id: z.number().int().positive(),
        step_id: z.number().int().positive().optional(),
        project_key: z.string().trim().min(1).optional(),
        issue_type: z.string().trim().min(1).optional(),
      }),
      response: JiraDraft,
    },
    create: {
      body: JiraIssueCreate,
      response: JiraIssue,
    },
    read: {
      params: z.object({ issue_id: z.number().int().positive() }),
      response: JiraIssue,
    },
  };

  public draft(
    query: z.input<typeof JiraIssuesEndpoint.schemas.draft.query>,
  ): ActionReturnType<z.infer<typeof JiraDraft>> {
    const valid = validateInput(JiraIssuesEndpoint.schemas.draft.query, query);
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["JiraIssues", "draft"],
      method: "get",
      path: [JiraIssuesEndpoint.basePath, "draft"],
      query: valid.data,
      localErrorHandling: true,
      response: JiraDraft,
    });
  }

  public create(body: z.input<typeof JiraIssuesEndpoint.schemas.create.body>): ActionReturnType<z.infer<typeof JiraIssue>> {
    const valid = validateInput(JiraIssuesEndpoint.schemas.create.body, body);
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["JiraIssues", "create"],
      method: "post",
      path: [JiraIssuesEndpoint.basePath],
      json: valid.data,
      localErrorHandling: true,
      response: JiraIssue,
    });
  }

  public read(issueId: number): ActionReturnType<z.infer<typeof JiraIssue>> {
    const params = validateInput(JiraIssuesEndpoint.schemas.read.params, { issue_id: issueId });
    if (!params.ok) return Promise.resolve(params);
    return request({
      log: ["JiraIssues", "read"],
      method: "get",
      path: [JiraIssuesEndpoint.basePath, params.data.issue_id],
      localErrorHandling: true,
      response: JiraIssue,
    });
  }
}
